import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/ui/Header';
import TicketFilters from './components/TicketFilters';
import TicketTable from './components/TicketTable';
import TicketDetailPanel from './components/TicketDetailPanel';
import TicketStats from './components/TicketStats';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CustomFieldsModal from './components/CustomFieldsModal';
import BulkOperationsBar from './components/BulkOperationsBar';
import BulkAssignModal from './components/BulkAssignModal';
import { supabase } from '../../lib/supabase';
import WeeklyCalendar from './components/WeeklyCalendar';
import SavedFilterTemplates from './components/SavedFilterTemplates';
import ConvertTicketToInvoiceModal from './components/ConvertTicketToInvoiceModal';
import CreateTicketModal from './components/CreateTicketModal';
import EditTicketModal from './components/EditTicketModal';
import { useAuth } from '../../contexts/AuthContext';


const TicketManagement = () => {
  const { orgRole, isSuperAdmin, isOrgAdmin } = useAuth();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customFieldsModal, setCustomFieldsModal] = useState({ isOpen: false, fieldType: null });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    technician: 'all',
    location: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    column: 'ticketId',
    direction: 'desc'
  });
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [convertInvoiceModal, setConvertInvoiceModal] = useState({ isOpen: false, ticket: null });
  const [invoiceSuccessBanner, setInvoiceSuccessBanner] = useState(null);
  const [createTicketModal, setCreateTicketModal] = useState(false);
  const [editTicketModal, setEditTicketModal] = useState({ isOpen: false, ticket: null });

  const ticketScrollRef = useRef(null);
  const calendarScrollRef = useRef(null);

  // Determine if user can open/edit tickets (all except customers)
  const isCustomer = orgRole === 'customer';
  const canManageTickets = isSuperAdmin || isOrgAdmin || (!isCustomer && !!orgRole);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setSyncing(true);
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('tickets')?.select(`
          *,
          customer:customers(*),
          service_type:ticket_service_types(*),
          priority:ticket_priorities(*),
          status:ticket_statuses(*),
          salesperson:personnel!tickets_salesperson_id_fkey(*),
          ticket_technicians(
            technician:personnel(*)
          ),
          ticket_products(*),
          ticket_communications(*),
          ticket_attachments(*),
          ticket_history(*)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      const formattedTickets = data?.map(ticket => ({
        id: ticket?.id,
        ticketId: ticket?.ticket_number,
        customer: ticket?.customer?.name,
        customerId: ticket?.customer?.id,
        customerEmail: ticket?.customer?.email,
        customerPhone: ticket?.customer?.phone,
        customerAddress: ticket?.customer?.address,
        locationId: ticket?.location_id || null,
        locationWellName: ticket?.customer_location?.well_name || null,
        locationCompany: ticket?.customer_location?.company || null,
        technicians: ticket?.ticket_technicians?.map(tt => tt?.technician?.name) || [],
        salesperson: ticket?.salesperson?.name || null,
        salespersonId: ticket?.salesperson?.id || null,
        serviceType: ticket?.service_type?.name,
        priority: ticket?.priority?.name,
        status: ticket?.status?.name,
        scheduledDate: ticket?.scheduled_date,
        estimatedDuration: ticket?.estimated_duration,
        description: ticket?.description,
        products: ticket?.ticket_products?.map(p => ({
          name: p?.name,
          quantity: p?.quantity,
          price: p?.price
        })) || [],
        communications: ticket?.ticket_communications?.map(c => ({
          id: c?.id,
          sender: c?.sender,
          message: c?.message,
          timestamp: c?.created_at
        })) || [],
        attachments: ticket?.ticket_attachments?.map(a => ({
          id: a?.id,
          name: a?.name,
          type: a?.type,
          url: a?.url,
          alt: a?.alt,
          size: a?.size
        })) || [],
        history: ticket?.ticket_history?.map(h => ({
          action: h?.action,
          description: h?.description,
          user: h?.user_name,
          timestamp: h?.created_at,
          icon: h?.icon
        })) || [],
        convertedToInvoice: ticket?.converted_to_invoice || false,
        invoiceId: ticket?.invoice_id || null
      })) || [];

      setTickets(formattedTickets);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error fetching tickets:', error?.message);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const [filteredTickets, setFilteredTickets] = useState([]);

  const stats = {
    total: tickets?.length || 0,
    inProgress: tickets?.filter(t => t?.status?.toLowerCase() === 'in progress')?.length || 0,
    pending: tickets?.filter(t => t?.status?.toLowerCase() === 'pending')?.length || 0,
    completed: tickets?.filter(t => t?.status?.toLowerCase() === 'completed')?.length || 0
  };

  const sortedTickets = [...filteredTickets]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.column];
    const bValue = b?.[sortConfig?.column];

    if (sortConfig?.column === 'scheduledDate') {
      return sortConfig?.direction === 'asc' ?
      new Date(aValue) - new Date(bValue) :
      new Date(bValue) - new Date(aValue);
    }

    if (typeof aValue === 'string') {
      return sortConfig?.direction === 'asc' ?
      aValue?.localeCompare(bValue) :
      bValue?.localeCompare(aValue);
    }

    return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  useEffect(() => {
    let filtered = [...tickets];

    if (filters?.search) {
      filtered = filtered?.filter(
        (ticket) =>
        ticket?.ticketId?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        ticket?.customer?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter((ticket) => ticket?.status?.toLowerCase() === filters?.status?.toLowerCase());
    }

    if (filters?.priority !== 'all') {
      filtered = filtered?.filter((ticket) => ticket?.priority?.toLowerCase() === filters?.priority?.toLowerCase());
    }

    if (filters?.technician !== 'all') {
      filtered = filtered?.filter((ticket) =>
      ticket?.technicians?.some((tech) => tech?.toLowerCase()?.includes(filters?.technician?.toLowerCase()))
      );
    }

    if (filters?.location !== 'all') {
      filtered = filtered?.filter((ticket) => ticket?.locationId === filters?.location);
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter((ticket) => new Date(ticket.scheduledDate) >= new Date(filters.dateFrom));
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter((ticket) => new Date(ticket.scheduledDate) <= new Date(filters.dateTo));
    }

    setFilteredTickets(filtered);
  }, [filters, sortConfig, tickets]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      priority: 'all',
      technician: 'all',
      location: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleCloseDetail = () => {
    setSelectedTicket(null);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      const statusMap = {
        'pending': 'Pending',
        'in-progress': 'In Progress',
        'on-hold': 'On Hold',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };

      const statusName = statusMap?.[newStatus] || newStatus;

      const { data: statusData, error: statusError } = await supabase?.from('ticket_statuses')?.select('id')?.eq('name', statusName)?.single();

      if (statusError) throw statusError;

      const { error: updateError } = await supabase?.from('tickets')?.update({ status_id: statusData?.id })?.eq('id', ticketId);

      if (updateError) throw updateError;

      // Send notification to managers when ticket is completed
      if (statusName === 'Completed') {
        await sendCompletionNotification(ticketId);
      }

      await fetchTickets();

      if (selectedTicket?.id === ticketId) {
        const updatedTicket = tickets?.find(t => t?.id === ticketId);
        if (updatedTicket) {
          setSelectedTicket({ ...updatedTicket, status: statusName });
        }
      }
    } catch (error) {
      console.error('Error updating status:', error?.message);
    }
  };

  const sendCompletionNotification = async (ticketId) => {
    try {
      const ticket = tickets?.find(t => t?.id === ticketId);
      if (!ticket) return;

      // Get all manager/admin personnel to notify
      const { data: managers } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.in('role', ['manager', 'admin', 'superadmin']);

      const notificationPayload = (managers || [])?.map(mgr => ({
        user_id: mgr?.id,
        title: 'Ticket Ready for Invoicing',
        message: `Ticket ${ticket?.ticketId} for ${ticket?.customer} has been completed. Please review and convert to an invoice.`,
        category: 'invoice',
        is_read: false,
        action_url: '/ticket-management',
        metadata: JSON.stringify({ ticket_id: ticketId, ticket_number: ticket?.ticketId })
      }));

      if (notificationPayload?.length > 0) {
        await supabase?.from('notifications')?.insert(notificationPayload);
      }
    } catch (err) {
      console.error('Error sending completion notification:', err?.message);
    }
  };

  const handleConvertToInvoice = (ticket) => {
    setConvertInvoiceModal({ isOpen: true, ticket });
  };

  const handleInvoiceCreated = (newInvoice) => {
    setInvoiceSuccessBanner(newInvoice);
    fetchTickets();
    setTimeout(() => setInvoiceSuccessBanner(null), 6000);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev =>
      prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = (tickets) => {
    const allSelected = tickets?.every(t => selectedIds?.includes(t?.id));
    if (allSelected) {
      setSelectedIds(prev => prev?.filter(id => !tickets?.some(t => t?.id === id)));
    } else {
      const newIds = tickets?.map(t => t?.id)?.filter(id => !selectedIds?.includes(id));
      setSelectedIds(prev => [...prev, ...newIds]);
    }
  };

  const handleBulkAssign = async (technician) => {
    try {
      for (const ticketId of selectedIds) {
        const { data: existing } = await supabase?.from('ticket_technicians')?.select('id')?.eq('ticket_id', ticketId)?.eq('technician_id', technician?.id)?.single();
        if (!existing) {
          await supabase?.from('ticket_technicians')?.insert({ ticket_id: ticketId, technician_id: technician?.id });
        }
      }
      await fetchTickets();
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk assign error:', error?.message);
    }
  };

  const handleBulkStatus = async (statusName) => {
    try {
      const { data: statusData, error: statusError } = await supabase?.from('ticket_statuses')?.select('id')?.eq('name', statusName)?.single();
      if (statusError) throw statusError;
      for (const ticketId of selectedIds) {
        await supabase?.from('tickets')?.update({ status_id: statusData?.id })?.eq('id', ticketId);
      }
      await fetchTickets();
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk status error:', error?.message);
    }
  };

  const handleBulkPriority = async (priorityName) => {
    try {
      const { data: priorityData, error: priorityError } = await supabase?.from('ticket_priorities')?.select('id')?.eq('name', priorityName)?.single();
      if (priorityError) throw priorityError;
      for (const ticketId of selectedIds) {
        await supabase?.from('tickets')?.update({ priority_id: priorityData?.id })?.eq('id', ticketId);
      }
      await fetchTickets();
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk priority error:', error?.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds?.length} ticket(s)? This cannot be undone.`)) return;
    try {
      for (const ticketId of selectedIds) {
        await supabase?.from('tickets')?.delete()?.eq('id', ticketId);
      }
      await fetchTickets();
      setSelectedIds([]);
      if (selectedIds?.includes(selectedTicket?.id)) setSelectedTicket(null);
    } catch (error) {
      console.error('Bulk delete error:', error?.message);
    }
  };

  const handleTicketReschedule = async (ticket, newDateISO) => {
    try {
      const { error } = await supabase
        ?.from('tickets')
        ?.update({ scheduled_date: newDateISO })
        ?.eq('id', ticket?.id);
      if (error) throw error;
      await fetchTickets();
    } catch (error) {
      console.error('Error rescheduling ticket:', error?.message);
    }
  };

  const scrollTickets = (direction) => {
    if (ticketScrollRef?.current) {
      ticketScrollRef?.current?.scrollBy({ top: direction === 'up' ? -200 : 200, behavior: 'smooth' });
    }
  };

  const scrollCalendar = (direction) => {
    if (calendarScrollRef?.current) {
      calendarScrollRef?.current?.scrollBy({ top: direction === 'up' ? -200 : 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8">
        {/* Invoice Success Banner */}
        {invoiceSuccessBanner && (
          <div className="mb-4 bg-success/10 border border-success/20 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <div>
                <p className="font-medium text-foreground">Invoice created successfully!</p>
                <p className="text-sm text-muted-foreground">Invoice #{invoiceSuccessBanner?.invoice_number} has been created and is ready for review.</p>
              </div>
            </div>
            <button onClick={() => setInvoiceSuccessBanner(null)} className="p-1 rounded hover:bg-muted">
              <Icon name="X" size={16} color="var(--color-muted-foreground)" />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
              Ticket Management
            </h1>
            <p className="text-muted-foreground">
              Track and manage service tickets with custom fields
            </p>
            {lastSyncTime && (
              <div className="flex items-center gap-2 mt-2">
                <Icon 
                  name={syncing ? "RefreshCw" : "CheckCircle"} 
                  size={14} 
                  color={syncing ? "var(--color-warning)" : "var(--color-success)"}
                  className={syncing ? "animate-spin" : ""}
                />
                <span className="text-xs text-muted-foreground">
                  {syncing ? 'Syncing...' : `Last synced: ${lastSyncTime?.toLocaleTimeString()}`}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={fetchTickets}
              disabled={syncing}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
            {canManageTickets && (
              <Button
                variant="default"
                onClick={() => setCreateTicketModal(true)}
                iconName="Plus"
                iconPosition="left"
              >
                Create Ticket
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setCustomFieldsModal({ isOpen: true, fieldType: 'serviceTypes' })}
              iconName="Wrench"
              iconPosition="left"
            >
              Service Types
            </Button>
            <Button
              variant="outline"
              onClick={() => setCustomFieldsModal({ isOpen: true, fieldType: 'statuses' })}
              iconName="Activity"
              iconPosition="left"
            >
              Statuses
            </Button>
            <Button
              variant="outline"
              onClick={() => setCustomFieldsModal({ isOpen: true, fieldType: 'priorities' })}
              iconName="AlertCircle"
              iconPosition="left"
            >
              Priorities
            </Button>
          </div>
        </div>

        {/* Stats */}
        <TicketStats stats={stats} />

        {/* Completed Tickets Pending Invoice Banner */}
        {tickets?.filter(t => t?.status?.toLowerCase() === 'completed' && !t?.convertedToInvoice)?.length > 0 && (
          <div className="mt-4 bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="FileCheck" size={20} color="var(--color-warning)" />
              <div>
                <p className="font-medium text-foreground">
                  {tickets?.filter(t => t?.status?.toLowerCase() === 'completed' && !t?.convertedToInvoice)?.length} completed ticket(s) pending invoice conversion
                </p>
                <p className="text-sm text-muted-foreground">Review completed tickets below and convert them to invoices.</p>
              </div>
            </div>
          </div>
        )}

        {/* Two-Column Layout */}
        <div className="flex flex-col xl:flex-row gap-6 mt-6">

          {/* LEFT COLUMN: Filters + Ticket List */}
          <div className="flex flex-col flex-1 min-w-0">
            {/* Column Header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="Ticket" size={18} color="var(--color-primary)" />
                Tickets
                <span className="text-sm font-normal text-muted-foreground">({sortedTickets?.length})</span>
              </h2>
              <div className="flex items-center gap-2">
                <SavedFilterTemplates
                  currentFilters={filters}
                  onLoadTemplate={(templateFilters) => setFilters(templateFilters)}
                />
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => scrollTickets('up')}
                    className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                    title="Scroll up"
                  >
                    <Icon name="ChevronUp" size={16} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    onClick={() => scrollTickets('down')}
                    className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                    title="Scroll down"
                  >
                    <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-4">
              <TicketFilters
                filters={filters}
                onFilterChange={setFilters}
                onReset={handleResetFilters}
              />
            </div>

            {/* Scrollable Ticket List */}
            <div
              ref={ticketScrollRef}
              className="overflow-y-auto rounded-xl border border-border bg-card"
              style={{ maxHeight: '70vh' }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
                </div>
              ) : sortedTickets?.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    No tickets found
                  </h3>
                  <p className="text-muted-foreground">
                    {filters?.search || filters?.status !== 'all' || filters?.priority !== 'all' ?'Try adjusting your filters' :'Create your first ticket to get started'}
                  </p>
                </div>
              ) : (
                <TicketTable
                  tickets={sortedTickets}
                  onTicketClick={setSelectedTicket}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onToggleSelectAll={handleToggleSelectAll}
                  onConvertToInvoice={handleConvertToInvoice}
                  onEditTicket={canManageTickets ? (ticket) => setEditTicketModal({ isOpen: true, ticket }) : undefined}
                />
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Weekly Calendar */}
          <div className="flex flex-col xl:w-[480px] 2xl:w-[560px] flex-shrink-0">
            {/* Column Header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="CalendarDays" size={18} color="var(--color-primary)" />
                Weekly Schedule
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => scrollCalendar('up')}
                  className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                  title="Scroll up"
                >
                  <Icon name="ChevronUp" size={16} color="var(--color-muted-foreground)" />
                </button>
                <button
                  onClick={() => scrollCalendar('down')}
                  className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                  title="Scroll down"
                >
                  <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>

            {/* Scrollable Calendar */}
            <div
              ref={calendarScrollRef}
              className="overflow-y-auto rounded-xl border border-border bg-card"
              style={{ maxHeight: '70vh' }}
            >
              <WeeklyCalendar
                tickets={tickets}
                onTicketClick={setSelectedTicket}
                onTicketReschedule={handleTicketReschedule}
              />
            </div>
          </div>

        </div>
      </main>

      {selectedTicket && (
        <TicketDetailPanel
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onStatusUpdate={handleStatusUpdate}
          onConvertToInvoice={handleConvertToInvoice}
        />
      )}
      <CustomFieldsModal
        isOpen={customFieldsModal?.isOpen}
        onClose={() => setCustomFieldsModal({ isOpen: false, fieldType: null })}
        fieldType={customFieldsModal?.fieldType}
      />
      <BulkOperationsBar
        selectedCount={selectedIds?.length}
        onBulkAssign={() => setBulkAssignModal(true)}
        onBulkStatus={handleBulkStatus}
        onBulkPriority={handleBulkPriority}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => setSelectedIds([])}
      />
      <BulkAssignModal
        isOpen={bulkAssignModal}
        onClose={() => setBulkAssignModal(false)}
        onAssign={handleBulkAssign}
        ticketCount={selectedIds?.length}
      />
      <ConvertTicketToInvoiceModal
        isOpen={convertInvoiceModal?.isOpen}
        ticket={convertInvoiceModal?.ticket}
        onClose={() => setConvertInvoiceModal({ isOpen: false, ticket: null })}
        onSuccess={handleInvoiceCreated}
      />
      <CreateTicketModal
        isOpen={createTicketModal}
        onClose={() => setCreateTicketModal(false)}
        onCreated={() => { fetchTickets(); setCreateTicketModal(false); }}
      />
      {editTicketModal?.isOpen && editTicketModal?.ticket && (
        <EditTicketModal
          ticket={editTicketModal?.ticket}
          isOpen={editTicketModal?.isOpen}
          onClose={() => setEditTicketModal({ isOpen: false, ticket: null })}
          onSaved={() => { fetchTickets(); setEditTicketModal({ isOpen: false, ticket: null }); }}
          orgRole={orgRole}
          isSuperAdmin={isSuperAdmin}
          isOrgAdmin={isOrgAdmin}
        />
      )}
    </div>
  );
};

export default TicketManagement;